/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { getMexAppVersionFacet } from "utils/webview"
import { storageHelper } from "utils/storage"
import { GRABX_FLAG_LIST } from "./constants"
import { FacetTypes, GrabXFeatureFlag, TFacets } from "./types"
import { generateDefaultFacets } from "./facets"
import { initializeScribeSDK, getFeature, buildFeatureFlags } from "./utils"

/**
 * Facets for one prefetched flag, resolved here rather than in `GRABX_FLAG_LIST`
 * because that list is a module-level const: `generateDefaultFacets()` inlined
 * there would read the user profile at import time, before login has written it.
 *
 * Returns undefined when the flag wants neither an explicit facet set nor the
 * device facet, so `getFeature` keeps applying the defaults itself.
 */
export const resolveFlagFacets = (
  grabxFlag: GrabXFeatureFlag,
): TFacets | undefined => {
  if (!grabxFlag.withDeviceFacet) {
    return grabxFlag.facets
  }

  return {
    ...(grabxFlag.facets ?? generateDefaultFacets()),
    // null outside the MEX app webview, which is what keeps a browser session
    // from matching an app-version rule by accident.
    [FacetTypes.Device]: getMexAppVersionFacet(),
  }
}

export const setupGrabXFeatureFlag = async () => {
  initializeScribeSDK()

  if (!GRABX_FLAG_LIST.length) {
    return
  }

  const promises = GRABX_FLAG_LIST.map(grabxFlag => {
    return getFeature(
      grabxFlag.grabxVariableName,
      grabxFlag.dataType,
      resolveFlagFacets(grabxFlag),
    )
  })

  const results = await Promise.allSettled(promises)
  const featureFlags = buildFeatureFlags(results)
  storageHelper.setMerchantPortalFeatureFlag(featureFlags)
}
